import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox } from '../../BasicComponents/Checkbox';
import { Skeleton } from '../../BasicComponents/Skeleton';
import { EmptyState } from '../../CompositeComponents/EmptyState';
import { Pagination } from '../Pagination';
import type { BulkAction, ColumnDef, DataTableProps, DataTableVariant, SortDirection } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

const SKELETON_ROWS = 5;

// ─── Variant cell padding ─────────────────────────────────────────────────────

const CELL_PAD: Record<DataTableVariant, string> = {
  default: 'px-4 py-3',
  compact: 'px-3 py-1.5',
  simple:  'px-4 py-3',
};

const CELL_TEXT: Record<DataTableVariant, string> = {
  default: 'text-sm',
  compact: 'text-xs',
  simple:  'text-sm',
};

// ─── Sort icons ───────────────────────────────────────────────────────────────

function SortIcon({ direction }: { direction: SortDirection | null }) {
  if (direction === 'asc') {
    return (
      <svg className="h-3.5 w-3.5 text-brand-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="18 15 12 9 6 15" />
      </svg>
    );
  }
  if (direction === 'desc') {
    return (
      <svg className="h-3.5 w-3.5 text-brand-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="6 9 12 15 18 9" />
      </svg>
    );
  }
  return (
    <svg className="h-3.5 w-3.5 text-neutral-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="8 9 12 5 16 9" />
      <polyline points="16 15 12 19 8 15" />
    </svg>
  );
}

// ─── Toolbar ──────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ToolbarProps<T extends {}> {
  columns: ColumnDef<T>[];
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onExport?: (() => void) | null;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
function Toolbar<T extends {}>({ columns, filters, onFilterChange, onExport }: ToolbarProps<T>) {
  const filterableCols = columns.filter((c) => c.filterable);
  if (filterableCols.length === 0 && !onExport) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
      {filterableCols.map((col) => (
        <input
          key={col.key}
          type="text"
          value={filters[col.key] ?? ''}
          placeholder={`Filter ${col.header}…`}
          aria-label={`Filter by ${col.header}`}
          onChange={(e) => onFilterChange(col.key, e.target.value)}
          className="h-8 rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-sm text-neutral-700 dark:text-neutral-200 placeholder:text-neutral-400 px-2.5 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
        />
      ))}
      {onExport && (
        <button
          type="button"
          onClick={onExport}
          className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <ExportIcon />
          Export CSV
        </button>
      )}
    </div>
  );
}

function ExportIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

// ─── Bulk action bar ──────────────────────────────────────────────────────────

function BulkBar({
  count,
  actions,
  onClear,
}: {
  count: number;
  actions: BulkAction[];
  onClear: () => void;
}) {
  return (
    <div
      role="toolbar"
      aria-label="Bulk actions"
      className="flex items-center gap-3 px-4 py-2.5 bg-brand-50 dark:bg-brand-900/20 border-b border-brand-200 dark:border-brand-800"
    >
      <span className="text-sm font-medium text-brand-700 dark:text-brand-300">
        {count} selected
      </span>
      <div className="flex items-center gap-2">
        {actions.map((action, i) => (
          <button
            key={i}
            type="button"
            onClick={() => action.onClick([])}
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded text-xs font-medium bg-white dark:bg-neutral-800 border border-brand-300 dark:border-brand-700 text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-900/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            {action.icon && <span className="h-3.5 w-3.5 flex items-center" aria-hidden="true">{action.icon}</span>}
            {action.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="ml-auto text-xs text-brand-600 dark:text-brand-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
        aria-label="Clear selection"
      >
        Clear
      </button>
    </div>
  );
}

// ─── DataTable ────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function DataTable<T extends {}>({
  columns,
  data,
  loading = false,
  error = null,
  emptyState,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  onSort,
  onFilter,
  pageSize = 20,
  totalCount,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
  bulkActions = [],
  stickyHeader = true,
  variant = 'default',
  onExport = null,
}: DataTableProps<T>) {
  // ── Sort state ──────────────────────────────────────────────────────────
  const [sortState, setSortState] = useState<{ column: string; direction: SortDirection } | null>(null);

  // ── Filter state + debounce ─────────────────────────────────────────────
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      const next = { ...filters, [key]: value };
      if (!value) delete next[key];
      setFilters(next);
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
      filterTimerRef.current = setTimeout(() => onFilter?.(next), 300);
    },
    [filters, onFilter],
  );

  useEffect(() => () => { if (filterTimerRef.current) clearTimeout(filterTimerRef.current); }, []);

  // ── Sort handler ────────────────────────────────────────────────────────
  const handleSort = useCallback(
    (colKey: string) => {
      setSortState((prev) => {
        if (!prev || prev.column !== colKey) {
          const next = { column: colKey, direction: 'asc' as SortDirection };
          onSort?.(colKey, 'asc');
          return next;
        }
        if (prev.direction === 'asc') {
          onSort?.(colKey, 'desc');
          return { column: colKey, direction: 'desc' };
        }
        // third click → clear sort
        onSort?.(colKey, 'asc'); // reset signal — caller clears their sort
        return null;
      });
    },
    [onSort],
  );

  // ── Selection helpers ───────────────────────────────────────────────────
  const allIds = data.map((r, i) => (r as { id?: string }).id ?? String(i));
  const selectedSet = new Set(selectedRows);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));
  const someSelected = !allSelected && allIds.some((id) => selectedSet.has(id));

  const toggleAll = useCallback(() => {
    if (allSelected) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.([...selectedSet, ...allIds.filter((id) => !selectedSet.has(id))]);
    }
  }, [allSelected, allIds, selectedSet, onSelectionChange]);

  const toggleRow = useCallback(
    (id: string) => {
      if (selectedSet.has(id)) {
        onSelectionChange?.(selectedRows.filter((s) => s !== id));
      } else {
        onSelectionChange?.([...selectedRows, id]);
      }
    },
    [selectedRows, selectedSet, onSelectionChange],
  );

  // ── Column count (for colSpan) ──────────────────────────────────────────
  const totalCols = columns.length + (selectable ? 1 : 0);

  // ── Styles ──────────────────────────────────────────────────────────────
  const cellPad = CELL_PAD[variant];
  const cellText = CELL_TEXT[variant];
  const isSimple = variant === 'simple';

  const thBase = [
    cellPad,
    'text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400',
    'border-b border-neutral-200 dark:border-neutral-700',
    'bg-white dark:bg-neutral-900',
    stickyHeader ? 'sticky top-0 z-10' : '',
  ].filter(Boolean).join(' ');

  const tdBase = [
    cellPad,
    cellText,
    'text-neutral-700 dark:text-neutral-200',
    'border-b border-neutral-100 dark:border-neutral-800',
  ].filter(Boolean).join(' ');

  // ── Pagination derived values ───────────────────────────────────────────
  const total = totalCount ?? data.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const showBulkBar = selectable && selectedRows.length > 0 && bulkActions.length > 0;

  return (
    <div className="flex flex-col w-full rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-white dark:bg-neutral-900">

      {/* Toolbar */}
      {!isSimple && (
        <Toolbar
          columns={columns}
          filters={filters}
          onFilterChange={handleFilterChange}
          onExport={onExport}
        />
      )}

      {/* Bulk action bar */}
      {showBulkBar && (
        <BulkBar
          count={selectedRows.length}
          actions={bulkActions.map((a) => ({
            ...a,
            onClick: () => a.onClick(selectedRows),
          }))}
          onClear={() => onSelectionChange?.([])}
        />
      )}

      {/* Error banner */}
      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 px-4 py-3 bg-danger-50 dark:bg-danger-900/20 border-b border-danger-200 dark:border-danger-800 text-sm text-danger-700 dark:text-danger-300"
        >
          <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* Table scroll wrapper */}
      <div className="overflow-x-auto flex-1">
        <table className="w-full border-collapse min-w-full">

          {/* Head */}
          <thead>
            <tr>
              {/* Select-all checkbox column */}
              {selectable && (
                <th
                  scope="col"
                  className={`${thBase} w-10 text-center`}
                  aria-label="Select all rows"
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    size="sm"
                    aria-label="Select all rows"
                  />
                </th>
              )}

              {columns.map((col) => {
                const isSorted = sortState?.column === col.key;
                const sortDir = isSorted ? sortState!.direction : null;
                const ariaSortVal: 'ascending' | 'descending' | 'none' = isSorted
                  ? sortDir === 'asc' ? 'ascending' : 'descending'
                  : 'none';

                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={col.sortable ? ariaSortVal : undefined}
                    style={col.width ? { width: col.width, minWidth: col.width } : undefined}
                    className={[
                      thBase,
                      col.numeric ? 'text-right' : 'text-left',
                      col.sortable ? 'cursor-pointer select-none' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  >
                    <span className={`inline-flex items-center gap-1 ${col.numeric ? 'justify-end w-full' : ''}`}>
                      {col.header}
                      {col.sortable && <SortIcon direction={sortDir} />}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {/* Loading skeleton rows */}
            {loading && Array.from({ length: SKELETON_ROWS }).map((_, i) => (
              <tr key={`skeleton-${i}`} aria-hidden="true">
                <td colSpan={totalCols} className={tdBase}>
                  <Skeleton variant="table-row" />
                </td>
              </tr>
            ))}

            {/* Empty state */}
            {!loading && data.length === 0 && !error && (
              <tr>
                <td colSpan={totalCols}>
                  <EmptyState
                    variant="table"
                    icon={emptyState?.icon}
                    title={emptyState?.title ?? 'No data'}
                    description={emptyState?.description}
                    action={
                      emptyState?.action
                        ? {
                            label: (emptyState.action as React.ReactElement<{ children?: React.ReactNode }>).props?.children?.toString() ?? 'Action',
                            onClick: (emptyState.action as React.ReactElement<{ onClick?: () => void }>).props?.onClick ?? (() => {}),
                          }
                        : undefined
                    }
                  />
                </td>
              </tr>
            )}

            {/* Data rows */}
            {!loading && data.map((row, rowIndex) => {
              const rowId = (row as { id?: string }).id ?? String(rowIndex);
              const isSelected = selectedSet.has(rowId);
              return (
                <tr
                  key={rowId}
                  aria-selected={selectable ? isSelected : undefined}
                  className={[
                    'transition-colors duration-100',
                    isSelected
                      ? 'bg-brand-50 dark:bg-brand-900/20'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50',
                  ].join(' ')}
                >
                  {/* Row checkbox */}
                  {selectable && (
                    <td className={`${tdBase} w-10 text-center`}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleRow(rowId)}
                        size="sm"
                        aria-label={`Select row ${rowId}`}
                      />
                    </td>
                  )}

                  {columns.map((col) => {
                    const raw = (row as Record<string, unknown>)[col.key];
                    const content = col.render
                      ? col.render(raw, row, rowIndex)
                      : raw !== null && raw !== undefined
                        ? String(raw)
                        : '—';

                    return (
                      <td
                        key={col.key}
                        className={[
                          tdBase,
                          col.numeric ? 'text-right tabular-nums font-medium' : '',
                        ].filter(Boolean).join(' ')}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {!isSimple && (onPageChange || totalCount !== undefined) && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 px-4 py-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={total}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
}
